import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, ListItem, ListItemButton, Switch, Tooltip, Typography, styled, useTheme } from "@mui/material";
import FormSubmitButton from "@/components/forms/FormSubmitButton";
import Form from "@/components/forms/Form";
import Select, { ISelectItem } from "@/components/forms/inputs/Select";
import { ConfigurationSourceData, ConfigurationSourceDataAllocation, Institution, Participant, ParticipantSourceDataAllocationUpdate } from "@/api/dtos";
import { IReturn } from "@servicestack/client";
import { GroupedVirtuoso } from "react-virtuoso";
import { ComponentProps, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Checkbox from "@/components/forms/inputs/Checkbox";
import { DateTime } from "luxon";
import { IFormSubmit, InputComponentType, OmitInputProps, useFormActions, useScopedField } from "@ngt/forms-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FormError } from "@/errors";
import { AppSettings } from "@/AppSettings";
import { DefaultSourceDataFormSettings, ISourceDataFormSettings } from "@/contexts/form/SourceDataFormSettingsContext";
import ChosenThemeSizeContext from "@/contexts/styles/ChosenThemeSizeContext";
import ThemeSize from "@/types/styles/ThemeSize";
import ChosenThemeSizeActionContext from "@/contexts/styles/ChosenThemeSizeActionContext";


const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogTitle-root': {
        padding: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'space-between',
        alignItems: 'center',

        '& > div': {
            display: 'flex',
            alignItems: 'center'
        }
    },
    '& .MuiDialogContent-root': {
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(0,-3),
}));


const FormButtons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'
}));

interface ISettingsDialogProps {
    open?: boolean;
    onClose?: () => void;
}

const ButtonSeparationDisplay = () => {
    const { state: { value: separateButtons } } = useScopedField<boolean>('separateButtons', { value: true });

    if (separateButtons) {
        return (
            <Box
                sx={{
                    minWidth: 170,
                    marginLeft: 'auto'
                }}
            >
                <Button 
                    variant="outlined" 
                    sx={{minWidth: 46, minHeight: 46, padding: 0, mr: 2}}
                >
                        <FontAwesomeIcon icon={AppSettings.icons.audit} size="2x"/>
                </Button>
                <Button 
                    variant="outlined" 
                    sx={{minWidth: 46, minHeight: 46, padding: 0, mr: 2}}
                >
                        <FontAwesomeIcon icon={AppSettings.icons.note} size="2x"/>
                </Button>
                <Button 
                    variant="outlined" 
                    sx={{minWidth: 46, minHeight: 46, padding: 0}}
                >
                        <FontAwesomeIcon icon={AppSettings.icons.finding} size="2x"/>
                </Button>
            </Box>
        )
    }


    return (
        <Box
            sx={{
                minWidth: 50,
                marginLeft: 'auto'
            }}
        >
            <Button 
                variant="outlined" 
                sx={{minWidth: 46, minHeight: 46, padding: 0}}
            >
                    <FontAwesomeIcon icon={AppSettings.icons.combined} size="2x"/>
            </Button>
        </Box>
    )
}

const sizeOptions = [
    {
        id: 'extra-small',
        name: 'Extra Small'
    },
    {
        id: 'small',
        name: 'Small'
    },
    {
        id: 'medium',
        name: 'medium'
    },
    {
        id: 'large',
        name: 'Large'
    },
    {
        id: 'extra-large',
        name: 'Extra Large'
    }
]

interface IForm {
    size: ThemeSize;
}

const SettingsDialog: FC<ISettingsDialogProps> = ({
    open,
    onClose
}) => {
    const size = useContext(ChosenThemeSizeContext)
    const setSize = useContext(ChosenThemeSizeActionContext)

    const submit = useCallback<IFormSubmit<IForm, FormError>>(async (formState, formActions) => {
        setSize(formState.value?.size ?? 'medium');
        onClose?.();

        return {};
    }, [onClose, setSize]);

    const initialData = useMemo(() => {
        return {
            size: size ?? 'medium'
        }
    }, [size, open])

    return (
        <Form
            initialValue={initialData}
            onSubmit={submit}
            //debug={AppSettings.debug}
        >
            <StyledDialog
                //onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open ?? false}
                maxWidth="xs"
                fullWidth
                scroll="paper"
            >
                <DialogTitle>
                    Settings
                </DialogTitle>
                <DialogContent dividers>
                    <Typography
                        variant="subtitle1"
                        color='primary'
                        gutterBottom
                    >
                        Layout Size
                    </Typography>
                    <Typography
                        variant="body1"
                        gutterBottom
                    >
                        Select the overall sizing of the UI.
                    </Typography>
                    <InputOnlyField
                        propertyPath="size"
                        label="Size"
                        component={Select}
                        options={sizeOptions}
                        nullOption={false}
                        sx={{
                            mt: 2,
                            mb: 3
                        }}
                    />
                </DialogContent>
                <DialogActions>
                        <FormButtons>
                            <FormSubmitButton
                                variant="contained"
                                color="primary"
                            >
                                Ok
                            </FormSubmitButton>
                            { 
                                onClose && (
                                    <Button
                                        variant="contained"
                                        color="inherit"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </Button>
                                )
                            }
                        </FormButtons>
                </DialogActions>
            </StyledDialog>
        </Form>
    );
}

export default SettingsDialog;